import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, ShieldCheck, Clock, History, AlertCircle, Check, Ban } from "lucide-react";
import { getToken } from "../../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
};

const DURATION_PRESETS = [
  { label: "7 days",    days: 7 },
  { label: "30 days",   days: 30 },
  { label: "3 months",  days: 90 },
  { label: "6 months",  days: 180 },
  { label: "1 year",    days: 365 },
];

/**
 * UserProModal — complimentary Pro access management for a single user.
 *
 *  - Reuses POST /api/admin/users/{id}/pro/{grant|extend|revoke}
 *  - Reuses GET  /api/admin/users/{id}/pro/history
 *  - Server-side already enforces admin auth, writes audit log, idempotently
 *    stacks days on the current pro_until (via grant_pro_days).
 *  - No Stripe transaction created — this is a manual complimentary grant.
 */
export default function UserProModal({ user, onClose, onChanged }) {
  const [mode, setMode] = useState("extend"); // extend | grant | revoke
  const [days, setDays] = useState(30);
  const [customDays, setCustomDays] = useState("");
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  const [history, setHistory] = useState(null);
  const [fresh, setFresh] = useState(user);

  const activePro = useMemo(() => {
    if (!fresh?.pro_until) return false;
    try { return new Date(fresh.pro_until) > new Date(); } catch { return false; }
  }, [fresh]);

  useEffect(() => {
    // Prime default mode: if user already has active Pro, suggest extend; else grant
    setMode(activePro ? "extend" : "grant");
  }, [activePro]);

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/admin/users/${user.user_id}/pro/history`, auth());
      setHistory(r.data?.history || []);
    } catch (e) {
      toast.error("Could not load history");
    }
  };

  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [user.user_id]);

  const effectiveDays = () => {
    if (mode === "revoke") return 0;
    const n = parseInt(customDays, 10);
    return Number.isFinite(n) && n > 0 ? n : days;
  };

  const confirmLabel = () => {
    const d = effectiveDays();
    if (mode === "revoke") return `Revoke Pro from ${user.email}`;
    if (mode === "grant")  return `Grant ${d} day${d === 1 ? "" : "s"} of Pro to ${user.email}`;
    return `Extend ${user.email}'s Pro by ${d} day${d === 1 ? "" : "s"}`;
  };

  const run = async () => {
    setWorking(true);
    try {
      const body = { reason: reason.trim() };
      if (mode !== "revoke") body.days = effectiveDays();
      const url = `${API}/admin/users/${user.user_id}/pro/${mode}`;
      const r = await axios.post(url, body, auth());
      const u = r.data?.user || r.data; // endpoint returns {user_id, role, pro_until, …}
      setFresh({ ...fresh, pro_until: u?.pro_until, role: u?.role, plan: (u?.pro_until && new Date(u.pro_until) > new Date()) ? "pro" : "free" });
      toast.success(mode === "revoke" ? "Pro access revoked" : `Pro access ${mode === "grant" ? "granted" : "extended"}`);
      setConfirming(false);
      setReason("");
      setCustomDays("");
      loadHistory();
      onChanged && onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Action failed";
      toast.error(typeof detail === "string" ? detail : "Action failed");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="user-pro-modal">
      <div className="bg-white border border-border max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/40">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-oxford" />
            <h3 className="text-sm font-semibold">Manage Pro Access</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-secondary" data-testid="pro-modal-close" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {/* -- Current status -- */}
        <div className="px-4 py-3 border-b border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">User</p>
          <p className="text-sm font-mono break-all">{fresh.email}</p>
          <p className="text-xs text-muted-foreground mt-0.5">user_id: {fresh.user_id}</p>

          <div className="grid grid-cols-3 gap-3 mt-3">
            <StatusTile label="Role" value={fresh.role || "—"} tone={fresh.role === "admin" ? "oxford" : "muted"} testid="pro-stat-role" />
            <StatusTile label="Plan" value={activePro ? "Pro" : "Free"} tone={activePro ? "green" : "muted"} testid="pro-stat-plan" />
            <StatusTile label="Expires" value={fresh.pro_until ? fmt(fresh.pro_until) : "—"} tone={activePro ? "green" : "muted"} testid="pro-stat-until" />
          </div>
        </div>

        {/* -- Action -- */}
        {!confirming && (
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Action</p>
            <div className="flex gap-2">
              {[
                { k: "grant", label: "Grant", icon: ShieldCheck },
                { k: "extend", label: "Extend", icon: Clock, disabled: !activePro },
                { k: "revoke", label: "Revoke", icon: Ban, disabled: !activePro },
              ].map((o) => {
                const Icon = o.icon;
                return (
                  <button key={o.k} onClick={() => setMode(o.k)} disabled={o.disabled}
                    data-testid={`pro-action-${o.k}`}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 border text-sm transition ${
                      mode === o.k ? "border-oxford bg-oxford/5 text-oxford font-medium" : "border-border hover:bg-secondary"
                    } ${o.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    <Icon size={14} />{o.label}
                  </button>
                );
              })}
            </div>

            {mode !== "revoke" && (
              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-1">Duration</p>
                <div className="flex flex-wrap gap-1.5">
                  {DURATION_PRESETS.map((p) => (
                    <button key={p.days} onClick={() => { setDays(p.days); setCustomDays(""); }}
                      className={`px-2 py-1 text-xs border ${days === p.days && !customDays ? "border-oxford bg-oxford/5" : "border-border hover:bg-secondary"}`}
                      data-testid={`pro-duration-${p.days}`}>
                      {p.label}
                    </button>
                  ))}
                  <div className="flex items-center gap-1">
                    <input type="number" min="1" max="3650" placeholder="custom"
                      value={customDays} onChange={(e) => setCustomDays(e.target.value)}
                      className="w-20 border border-border px-2 py-1 text-xs"
                      data-testid="pro-duration-custom" />
                    <span className="text-xs text-muted-foreground">days</span>
                  </div>
                </div>
                {mode === "extend" && activePro && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Extends from current expiry ({fmt(fresh.pro_until)}) — not from today.
                  </p>
                )}
              </div>
            )}

            <div className="mt-3">
              <p className="text-xs text-muted-foreground mb-1">Audit reason (optional but recommended)</p>
              <input value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Complimentary — partner event attendee"
                className="w-full border border-border px-2 py-1.5 text-sm"
                data-testid="pro-reason" />
            </div>

            <div className="mt-3 flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">No charge or Stripe transaction will be created.</p>
              <button onClick={() => setConfirming(true)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white ${
                  mode === "revoke" ? "bg-[#B91C1C] hover:bg-[#991B1B]" : "bg-oxford hover:brightness-110"
                }`}
                data-testid="pro-review">
                Review change →
              </button>
            </div>
          </div>
        )}

        {/* -- Confirmation -- */}
        {confirming && (
          <div className="px-4 py-3 border-b border-border bg-amber-50" data-testid="pro-confirm">
            <div className="flex items-start gap-2 mb-2">
              <AlertCircle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-900">Confirm this change</p>
                <p className="text-sm text-amber-900 mt-0.5">{confirmLabel()}</p>
                {reason.trim() && <p className="text-xs text-amber-700 mt-1 italic">reason: "{reason.trim()}"</p>}
                {!reason.trim() && <p className="text-xs text-amber-700 mt-1 italic">(no audit reason — the audit entry will still record you + the action)</p>}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-2">
              <button onClick={() => setConfirming(false)} disabled={working}
                className="px-3 py-1.5 text-sm border border-border hover:bg-secondary" data-testid="pro-cancel">
                Cancel
              </button>
              <button onClick={run} disabled={working}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${
                  mode === "revoke" ? "bg-[#B91C1C] hover:bg-[#991B1B]" : "bg-oxford hover:brightness-110"
                }`}
                data-testid="pro-confirm-apply">
                {working ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
                Confirm {mode === "revoke" ? "revoke" : mode}
              </button>
            </div>
          </div>
        )}

        {/* -- Audit history -- */}
        <div className="px-4 py-3">
          <div className="flex items-center gap-1.5 mb-2">
            <History size={14} className="text-muted-foreground" />
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Audit history</p>
          </div>
          {history === null && <Loader2 className="animate-spin" size={14} />}
          {history !== null && history.length === 0 && (
            <p className="text-xs text-muted-foreground italic">No prior admin actions on this user's Pro access.</p>
          )}
          {history !== null && history.length > 0 && (
            <div className="max-h-56 overflow-y-auto border border-border divide-y divide-border" data-testid="pro-history">
              {history.map((h, i) => (
                <div key={i} className="px-3 py-2 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono">{h.type.replace("user_pro.", "")}</span>
                    <span className="text-muted-foreground">{fmt(h.created_at)}</span>
                  </div>
                  <div className="text-muted-foreground mt-0.5">by {h.actor_email || h.actor || "unknown admin"}</div>
                  {h.reason && <div className="text-zinc-700 italic mt-0.5">"{h.reason}"</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const StatusTile = ({ label, value, tone = "muted", testid }) => {
  const toneClass = {
    oxford: "border-oxford text-oxford",
    green:  "border-[#15803D] text-[#15803D]",
    muted:  "border-border text-muted-foreground",
  }[tone];
  return (
    <div className={`border ${toneClass} p-2`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider opacity-70">{label}</p>
      <p className="text-sm font-medium mt-0.5 break-words">{value}</p>
    </div>
  );
};
